import { Logger } from './Logger';
import { LocalStorageAuth } from './LocalStorageAuth';
import { CookieUtils } from './CookieUtils';
import { COOKIE_TOKEN_ID } from '../envConfig';

export const AuthMigration = {
    /**
     * Migrate authentication data from cookies to local storage
     * This is a one-time migration to help existing users
     */
    migrateFromCookies: (): boolean => {
        try {
            Logger.debug('AuthMigration', 'Starting migration from cookies to local storage');
            
            // Check if we already have data in local storage
            if (LocalStorageAuth.isAuthenticated()) {
                Logger.debug('AuthMigration', 'Local storage already has authentication data, skipping migration');
                return true;
            }
            
            // Try to get token from cookies
            const cookieToken = CookieUtils.getCookie(COOKIE_TOKEN_ID);
            
            if (cookieToken) {
                Logger.debug('AuthMigration', 'Found token in cookies, migrating to local storage');
                
                // Store the token in local storage
                LocalStorageAuth.setToken(cookieToken);
                
                // Clear the cookie after successful migration
                CookieUtils.deleteCookie(COOKIE_TOKEN_ID);
                
                Logger.debug('AuthMigration', 'Successfully migrated token from cookies to local storage');
                return true;
            } else {
                Logger.debug('AuthMigration', 'No token found in cookies, nothing to migrate');
                return false;
            }
        } catch (error) {
            Logger.error('AuthMigration', `Migration failed: ${error}`);
            return false;
        }
    },

    /**
     * Check if migration is needed
     */
    isMigrationNeeded: (): boolean => {
        // Migration is needed if we have cookies but no local storage data
        const hasCookieToken = !!CookieUtils.getCookie(COOKIE_TOKEN_ID);
        const hasLocalStorageToken = LocalStorageAuth.isAuthenticated();
        
        return hasCookieToken && !hasLocalStorageToken;
    },

    /**
     * Perform migration if needed
     */
    migrateIfNeeded: (): boolean => {
        if (AuthMigration.isMigrationNeeded()) {
            Logger.info('AuthMigration', 'Migration needed, starting migration process');
            return AuthMigration.migrateFromCookies();
        }
        
        Logger.debug('AuthMigration', 'No migration needed');
        return false;
    }
};
